/**
 * The component library.
 *
 * Props are semantic (`tone="critical"`), not presentational (`color="red"`), so
 * a theme change never means editing call sites. Variants are enums rather than
 * combining booleans.
 */

import {
  forwardRef,
  useEffect,
  useId,
  useRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { formatMoney, formatSignedMoney, type TransactionFlow } from '@alohamula/domain';
import { WarningIcon } from '../icons';
import './ui.css';

// ---------------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------------

type ButtonEmphasis = 'primary' | 'secondary' | 'ghost' | 'danger';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  emphasis?: ButtonEmphasis;
  size?: 'md' | 'sm';
  block?: boolean;
  /** Shows a spinner and blocks input. Keeps the label so width does not jump. */
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { emphasis = 'secondary', size = 'md', block, loading, children, className, disabled, ...rest },
  ref
) {
  return (
    <button
      {...rest}
      ref={ref}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={[
        'btn',
        `btn--${emphasis}`,
        size === 'sm' ? 'btn--sm' : '',
        block ? 'btn--block' : '',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {loading && <span className="btn__spinner" aria-hidden="true" />}
      {children}
    </button>
  );
});

// ---------------------------------------------------------------------------
// Money
// ---------------------------------------------------------------------------

interface MoneyProps {
  value: number;
  /** Shows an explicit + or −, for ledger rows where direction matters. */
  signed?: boolean;
  /**
   * Colors the amount by what the money did: money in is valley green, your own
   * money moving is moana blue, ordinary spending stays ink. Takes precedence
   * over `tone`, since what happened outranks how a screen feels about it.
   */
  flow?: TransactionFlow;
  /** `over` is the one alarming state: a budget item past its funding. */
  tone?: 'default' | 'over' | 'inherit';
  className?: string;
}

function moneyToneClass(flow: TransactionFlow | undefined, tone: MoneyProps['tone']): string {
  if (flow) {
    switch (flow) {
      case 'deposit':
      case 'refund':
      case 'transferIn':
        return 'money--in';
      case 'transferOut':
      case 'withdrawal':
      case 'sent':
        return 'money--movement';
      case 'spending':
        return 'money--spending';
    }
  }
  if (tone === 'over') return 'money--over';
  if (tone === 'inherit') return '';
  return 'money--spending';
}

export function Money({ value, signed, flow, tone = 'default', className }: MoneyProps) {
  return (
    <span className={['money', moneyToneClass(flow, tone), className ?? ''].filter(Boolean).join(' ')}>
      {signed ? formatSignedMoney(value) : formatMoney(value)}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Meter
// ---------------------------------------------------------------------------

interface MeterProps {
  /** 0–1. */
  progress: number;
  variant?: 'envelope' | 'bill';
  status?: 'normal' | 'low' | 'over';
  label: string;
}

export function Meter({ progress, variant = 'envelope', status = 'normal', label }: MeterProps) {
  const fill = status === 'over' ? 1 : Math.min(Math.max(progress, 0), 1);
  const statusClass =
    status === 'over' ? 'meter__fill--over' : status === 'low' ? 'meter__fill--low' : '';
  return (
    <div
      className="meter"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(fill * 100)}
      aria-label={label}
    >
      <div
        className={[
          'meter__fill',
          variant === 'bill' && !statusClass ? 'meter__fill--bill' : '',
          statusClass,
        ]
          .filter(Boolean)
          .join(' ')}
        style={{ width: `${fill * 100}%` }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Badge
// ---------------------------------------------------------------------------

export function Badge({
  tone = 'neutral',
  children,
}: {
  tone?: 'neutral' | 'danger' | 'warning' | 'success' | 'action';
  children: ReactNode;
}) {
  return <span className={`badge badge--${tone}`}>{children}</span>;
}

export function Dot({ color }: { color: string }) {
  return <span className="dot" style={{ background: color }} aria-hidden="true" />;
}

// ---------------------------------------------------------------------------
// Toggle
// ---------------------------------------------------------------------------

/**
 * An on/off switch for a setting that applies immediately in the form.
 *
 * A real checkbox with `role="switch"` underneath: the whole row is a `<label>`,
 * so the hint text is part of the hit target and screen readers read the label
 * with the state, without any aria wiring at the call site.
 */
export function Toggle({
  label,
  hint,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  hint?: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className={`toggle${disabled ? ' is-disabled' : ''}`}>
      <span className="toggle__text">
        <span className="toggle__label">{label}</span>
        {hint && <span className="toggle__hint">{hint}</span>}
      </span>
      <input
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="toggle__track" aria-hidden="true">
        <span className="toggle__knob" />
      </span>
    </label>
  );
}

// ---------------------------------------------------------------------------
// Segmented control
// ---------------------------------------------------------------------------

/**
 * A small, fixed set of choices where seeing the alternatives is the point.
 *
 * Built on radio inputs rather than buttons so arrow keys move between options
 * and assistive tech announces "2 of 3" without any custom key handling; the
 * inputs are visually hidden but still the thing being focused.
 */
export function SegmentedControl<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: readonly { value: T; label: string }[];
  onChange: (value: T) => void;
}) {
  const name = useId();

  return (
    <div className="segmented" role="radiogroup" aria-label={label}>
      {options.map((option) => (
        <label
          key={option.value}
          className={`segmented__option${option.value === value ? ' is-selected' : ''}`}
        >
          <input
            type="radio"
            name={name}
            value={option.value}
            checked={option.value === value}
            onChange={() => onChange(option.value)}
          />
          <span>{option.label}</span>
        </label>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fields
// ---------------------------------------------------------------------------

interface FieldProps {
  label: string;
  /** Neutral, unchanging help. Never used to report that something is wrong. */
  hint?: string;
  /**
   * The value is usable but disagrees with something else, and the person should
   * know. Distinct from `error`, which means the value cannot be saved.
   */
  warning?: string;
  error?: string;
  children: (props: { id: string; describedBy?: string; invalid: boolean }) => ReactNode;
}

/**
 * Owns the label/hint/error wiring so no call site has to remember
 * `aria-describedby`. Errors say what is wrong, and the field keeps its value.
 *
 * A warning is deliberately not a hint: dressing "your plan disagrees with your
 * envelopes" in the same grey as "what lands on the 1st and the 15th" is how a
 * real problem goes unread.
 */
export function Field({ label, hint, warning, error, children }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const warningId = `${id}-warning`;
  const errorId = `${id}-error`;
  const describedBy = [hint ? hintId : '', warning ? warningId : '', error ? errorId : '']
    .filter(Boolean)
    .join(' ');

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      {hint && (
        <span className="field__hint" id={hintId}>
          {hint}
        </span>
      )}
      {children({ id, describedBy: describedBy || undefined, invalid: Boolean(error) })}
      {warning && (
        <span className="field__warning" id={warningId}>
          <WarningIcon width={14} height={14} aria-hidden="true" />
          {warning}
        </span>
      )}
      {error && (
        <span className="field__error" id={errorId} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...rest }, ref) {
    return <input {...rest} ref={ref} className={['input', className ?? ''].filter(Boolean).join(' ')} />;
  }
);

export const MoneyInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function MoneyInput({ className, ...rest }, ref) {
    return (
      <input
        {...rest}
        ref={ref}
        // `text` with a numeric keypad: `type="number"` scroll-wheels values and
        // rejects a leading "$" that people paste in.
        type="text"
        inputMode="decimal"
        autoComplete="off"
        className={['input', 'input--money', className ?? ''].filter(Boolean).join(' ')}
      />
    );
  }
);

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, ...rest }, ref) {
    return (
      <select {...rest} ref={ref} className={['select', className ?? ''].filter(Boolean).join(' ')} />
    );
  }
);

// ---------------------------------------------------------------------------
// States
// ---------------------------------------------------------------------------

/** An empty state always offers the next action; it never just says "nothing here". */
export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body?: string;
  action?: ReactNode;
}) {
  return (
    <div className="state">
      <p className="state__title">{title}</p>
      {body && <p className="state__body">{body}</p>}
      {action}
    </div>
  );
}

export function ErrorState({
  title,
  body,
  onRetry,
}: {
  title: string;
  body?: string;
  onRetry?: () => void;
}) {
  return (
    <div className="state" role="alert">
      <p className="state__title">{title}</p>
      {body && <p className="state__body">{body}</p>}
      {onRetry && (
        <Button emphasis="primary" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

export function Skeleton({ height, width }: { height: number | string; width?: number | string }) {
  return <div className="skeleton" style={{ height, width: width ?? '100%' }} aria-hidden="true" />;
}

// ---------------------------------------------------------------------------
// Dialog
// ---------------------------------------------------------------------------

interface DialogProps {
  open: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}

/**
 * Native `<dialog>`: the browser gives focus trapping, Escape to close, and
 * inert background for free, and gets them right more reliably than a hand-rolled
 * modal would.
 */
export function Dialog({ open, title, subtitle, onClose, children, footer }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    if (open && !element.open) element.showModal();
    if (!open && element.open) element.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      className="dialog"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      // A click on the backdrop lands on the dialog element itself.
      onClick={(event) => {
        if (event.target === ref.current) onClose();
      }}
    >
      <div className="dialog__layout">
        <header className="dialog__header">
          <div>
            <h2 className="dialog__title">{title}</h2>
            {subtitle && <p className="dialog__subtitle">{subtitle}</p>}
          </div>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close">
            <CloseIcon />
          </button>
        </header>
        <div className="dialog__body">{children}</div>
        {footer && <footer className="dialog__footer">{footer}</footer>}
      </div>
    </dialog>
  );
}

function CloseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M4 4l8 8M12 4l-8 8"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------

export function Section({
  title,
  meta,
  action,
  children,
}: {
  title: string;
  meta?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="section">
      <div className="section__header">
        <h2 className="section__title">{title}</h2>
        {action ?? (meta ? <span className="section__meta">{meta}</span> : null)}
      </div>
      {children}
    </section>
  );
}
